/**
 * Wizard para Configuração do Contexto de Produção
 * 
 * Um assistente passo-a-passo para guiar o usuário na configuração
 * do contexto de produção (clone, sistema de sangria, árvores, etc.)
 * 
 * Princípios do Tripé:
 * - Justiça: Explicações claras sobre o impacto de cada escolha
 * - Segurança: Validação de dados em cada passo
 * - Simplicidade: Interface limpa e intuitiva
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import { useTranslation } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuração dos passos do wizard - títulos serão traduzidos dinamicamente
const WIZARD_STEPS_CONFIG = [
  { id: 'property', titleKey: 'wizardStepProperty', descKey: 'wizardStepPropertyDesc', icon: '🏠' },
  { id: 'clone', titleKey: 'wizardStepClone', descKey: 'wizardStepCloneDesc', icon: '🌳' },
  { id: 'system', titleKey: 'wizardStepSystem', descKey: 'wizardStepSystemDesc', icon: '🔄' },
  { id: 'trees', titleKey: 'wizardStepTrees', descKey: 'wizardStepTreesDesc', icon: '📊' },
  { id: 'extras', titleKey: 'wizardStepExtras', descKey: 'wizardStepExtrasDesc', icon: '⚙️' },
  { id: 'confirm', titleKey: 'wizardStepConfirm', descKey: 'wizardStepConfirmDesc', icon: '✅' }
];

const ProductionContextWizard = ({ 
  properties, 
  contexts, 
  clones, 
  tappingSystems, 
  onComplete, 
  onCancel,
  isOpen 
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Passos traduzidos
  const WIZARD_STEPS = WIZARD_STEPS_CONFIG.map(step => ({
    ...step,
    title: t(step.titleKey) || step.id,
    description: t(step.descKey) || ''
  }));
  // Form data
  const [formData, setFormData] = useState({
    property_id: '',
    property_name: '',
    clone_type: '',
    clones: [],  // [{name, percentage}]
    tapping_system: '',
    tree_density: 0,
    trees_in_production: 0,
    uses_stimulation: false,
    stimulation_percentage: null,
    seringal_age_years: 7,
    region: 'sp',
    drc_percentage: 53
  });

  // Propriedades sem contexto
  const propertiesWithoutContext = properties.filter(
    p => !contexts.some(c => c.property_id === p.id)
  );

  // Progresso do wizard
  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  // Validação por passo
  const isStepValid = (step) => {
    switch (step) {
      case 0: // property
        return formData.property_id !== '';
      case 1: // clone
        // Válido se tem pelo menos 1 clone com porcentagem e total = 100%
        if (formData.clones.length === 0) return false;
        const totalPct = formData.clones.reduce((sum, c) => sum + (c.percentage || 0), 0);
        return totalPct === 100;
      case 2: // system
        return formData.tapping_system !== '';
      case 3: // trees
        return formData.trees_in_production > 0 && formData.tree_density > 0;
      case 4: // extras
        return formData.region !== '';
      case 5: // confirm
        return true;
      default:
        return false;
    }
  };

  // Navegação
  const nextStep = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (index) => {
    // Só permite ir para passos anteriores ou o próximo válido
    if (index <= currentStep || (index === currentStep + 1 && isStepValid(currentStep))) {
      setCurrentStep(index);
    }
  };

  // Seleção de propriedade
  const handlePropertySelect = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    setFormData({
      ...formData,
      property_id: propertyId,
      property_name: property?.name || '',
      // Tenta pré-preencher com dados da propriedade
      seringal_age_years: property?.culture_age_years || 7,
      tapping_system: property?.sistema_sangria || ''
    });
  };

  // Seleção de clone - multi-clone
  const handleCloneToggle = (cloneName) => {
    setFormData(prev => {
      const existing = prev.clones.find(c => c.name === cloneName);
      let newClones;
      if (existing) {
        // Remove o clone
        newClones = prev.clones.filter(c => c.name !== cloneName);
        // Redistribuir % proporcionalmente entre os restantes
        if (newClones.length > 0) {
          const totalRemaining = newClones.reduce((s, c) => s + (c.percentage || 0), 0);
          if (totalRemaining > 0) {
            // Ajustar proporcionalmente para totalizar 100%
            const factor = 100 / totalRemaining;
            newClones = newClones.map(c => ({ ...c, percentage: Math.round(c.percentage * factor) }));
            // Corrigir arredondamento para exatamente 100%
            const diff = 100 - newClones.reduce((s, c) => s + c.percentage, 0);
            if (diff !== 0) newClones[0].percentage += diff;
          } else if (newClones.length === 1) {
            newClones[0].percentage = 100;
          }
        }
      } else {
        // Adicionar novo clone
        if (prev.clones.length === 0) {
          // Primeiro clone: 100%
          newClones = [{ name: cloneName, percentage: 100 }];
        } else {
          // Redistribuir igualmente
          const count = prev.clones.length + 1;
          const equalPct = Math.floor(100 / count);
          const remainder = 100 - (equalPct * count);
          newClones = prev.clones.map(c => ({ ...c, percentage: equalPct }));
          newClones.push({ name: cloneName, percentage: equalPct + remainder });
        }
      }
      // Clone dominante = maior %
      const dominant = newClones.length > 0 
        ? newClones.reduce((a, b) => (a.percentage || 0) >= (b.percentage || 0) ? a : b).name 
        : '';
      return { ...prev, clones: newClones, clone_type: dominant };
    });
  };

  const handleClonePercentage = (cloneName, pct) => {
    setFormData(prev => {
      const newClones = prev.clones.map(c => 
        c.name === cloneName ? { ...c, percentage: Math.max(0, Math.min(100, Number(pct) || 0)) } : c
      );
      const dominant = newClones.length > 0 
        ? newClones.reduce((a, b) => (a.percentage || 0) >= (b.percentage || 0) ? a : b).name 
        : '';
      return { ...prev, clones: newClones, clone_type: dominant };
    });
  };

  const handleClearAllClones = () => {
    setFormData(prev => ({ ...prev, clones: [], clone_type: '' }));
  };

  // Submit final
  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/contextual-performance/contexts`, formData);
      window.dispatchEvent(new Event('heveatalent-progress-update'));
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar contexto');
    }
    
    setSaving(false);
  };

  // Reset wizard
  const resetWizard = () => {
    setCurrentStep(0);
    setFormData({
      property_id: '',
      property_name: '',
      clone_type: '',
      clones: [],
      tapping_system: '',
      tree_density: 0,
      trees_in_production: 0,
      uses_stimulation: false,
      stimulation_percentage: null,
      seringal_age_years: 7,
      region: 'sp',
      drc_percentage: 53
    });
    setError(null);
  };

  // Componente para cada passo
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <PropertyStep 
          formData={formData} 
          properties={propertiesWithoutContext}
          onSelect={handlePropertySelect}
        />;
      case 1:
        return <CloneStep 
          formData={formData} 
          clones={clones}
          onToggle={handleCloneToggle}
          onPercentage={handleClonePercentage}
          onClearAll={handleClearAllClones}
        />;
      case 2:
        return <SystemStep 
          formData={formData}
          tappingSystems={tappingSystems}
          onSelect={(system) => setFormData(prev => ({...prev, tapping_system: system}))}
        />;
      case 3:
        return <TreesStep 
          formData={formData}
          onChange={(key, value) => setFormData(prev => ({...prev, [key]: value}))}
        />;
      case 4:
        return <ExtrasStep 
          formData={formData}
          onChange={(key, value) => setFormData(prev => ({...prev, [key]: value}))}
        />;
      case 5:
        return <ConfirmStep 
          formData={formData}
          clones={clones}
          tappingSystems={tappingSystems}
        />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Se não há propriedades sem contexto
  if (propertiesWithoutContext.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="text-4xl mb-2">✅</div>
            <CardTitle>{t('wizardAllConfigured') || 'Tudo Configurado!'}</CardTitle>
            <CardDescription>
              {t('wizardAllConfiguredDesc') || 'Todas as suas propriedades já têm contexto de produção configurado.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onCancel} className="w-full">
              {t('wizardClose') || 'Fechar'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8 max-h-[90vh] flex flex-col" data-testid="context-wizard">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <span className="text-2xl">⚙️</span>
                {t('wizardTitle') || 'Configurar Contexto de Produção'}
              </CardTitle>
              <CardDescription className="text-emerald-100 mt-1">
                {(t('wizardStepOf') || 'Passo {current} de {total}').replace('{current}', currentStep + 1).replace('{total}', WIZARD_STEPS.length)}: {WIZARD_STEPS[currentStep].title}
              </CardDescription>
            </div>
            <button 
              onClick={onCancel}
              className="text-white/70 hover:text-white text-xl"
              data-testid="wizard-close-btn"
            >
              ✕
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2 bg-white/20" />
          </div>
        </CardHeader>

        {/* Step indicators */}
        <div className="flex justify-between px-6 py-3 bg-gray-50 border-b overflow-x-auto">
          {WIZARD_STEPS.map((step, index) => (
            <button
              key={step.id}
              onClick={() => goToStep(index)}
              className={`flex flex-col items-center min-w-[60px] transition-all ${
                index === currentStep 
                  ? 'text-emerald-600 scale-105' 
                  : index < currentStep 
                    ? 'text-green-500' 
                    : 'text-gray-400'
              }`}
              disabled={index > currentStep + 1}
              data-testid={`wizard-step-${step.id}`}
            >
              <span className={`text-lg mb-1 ${
                index < currentStep ? 'bg-green-100 rounded-full p-1' : ''
              }`}>
                {index < currentStep ? '✓' : step.icon}
              </span>
              <span className="text-xs font-medium hidden sm:block">{step.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <CardContent className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {renderStepContent()}
        </CardContent>

        {/* Footer with navigation */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-lg">
          <Button 
            variant="outline" 
            onClick={currentStep === 0 ? onCancel : prevStep}
            data-testid="wizard-prev-btn"
          >
            {currentStep === 0 ? (t('wizardCancel') || 'Cancelar') : `← ${t('wizardBack') || 'Voltar'}`}
          </Button>
          
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <Button 
              onClick={nextStep}
              disabled={!isStepValid(currentStep)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wizard-next-btn"
            >
              {t('wizardNext') || 'Próximo'} →
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wizard-save-btn"
            >
              {saving ? (t('wizardSaving') || 'Salvando...') : `✓ ${t('wizardSave') || 'Salvar Contexto'}`}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// STEP COMPONENTS
// =============================================================================

const PropertyStep = ({ formData, properties, onSelect }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <span className="text-4xl">🏠</span>
        <h3 className="text-lg font-semibold mt-2">{t('wizardSelectProperty') || 'Selecione a Propriedade'}</h3>
        <p className="text-gray-600 text-sm">
          {t('wizardSelectPropertyDesc') || 'Escolha a propriedade para configurar o contexto de produção'}
        </p>
      </div>

      {properties.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>{t('wizardNoPropertiesAvailable') || 'Nenhuma propriedade disponível para configurar.'}</p>
          <p className="text-sm mt-2">{t('wizardNoPropertiesHint') || 'Todas já têm contexto ou você precisa cadastrar uma propriedade primeiro.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {properties.map(prop => (
            <button
              key={prop.id}
              onClick={() => onSelect(prop.id)}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                formData.property_id === prop.id
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`property-option-${prop.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{prop.name}</div>
                  <div className="text-sm text-gray-500">
                    {prop.location} • {prop.culture_age_years || '?'} {t('years') || 'anos'}
                  </div>
                </div>
                {formData.property_id === prop.id && (
                  <span className="text-emerald-500 text-xl">✓</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CloneStep = ({ formData, clones, onToggle, onPercentage, onClearAll }) => {
  const { t } = useTranslation();
  
  // Clones populares primeiro
  const popularClones = ['RRIM 600', 'GT 1', 'PB 235', 'PB 217'];
  const sortedClones = [...clones].sort((a, b) => {
    const aPopular = popularClones.indexOf(a.name);
    const bPopular = popularClones.indexOf(b.name);
    if (aPopular !== -1 && bPopular !== -1) return aPopular - bPopular;
    if (aPopular !== -1) return -1;
    if (bPopular !== -1) return 1;
    return 0;
  });

  const selectedClones = formData.clones || [];
  const totalPct = selectedClones.reduce((sum, c) => sum + (c.percentage || 0), 0);
  const isSelected = (name) => selectedClones.some(c => c.name === name);

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold mt-2">{t('wizardWhichClone') || 'Quais os Clones da Propriedade?'}</h3>
        <p className="text-gray-600 text-sm">
          Selecione os clones. As porcentagens sao distribuidas automaticamente.
        </p>
      </div>

      {/* Clones selecionados com porcentagem */}
      {selectedClones.length > 0 && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-emerald-800 text-sm">
              Clones selecionados ({selectedClones.length})
            </span>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${totalPct === 100 ? 'text-emerald-600' : 'text-red-600'}`}>
                Total: {totalPct}% {totalPct === 100 ? '\u2713' : `(faltam ${100 - totalPct}%)`}
              </span>
              <button
                onClick={onClearAll}
                className="text-xs text-red-600 hover:text-red-800 underline px-2 py-1"
                data-testid="clear-all-clones"
              >
                Limpar tudo
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {selectedClones.map(sc => {
              const cloneInfo = clones.find(c => c.name === sc.name);
              return (
                <div key={sc.name} className="flex items-center gap-2 bg-white p-2 rounded border border-emerald-200">
                  <span className="font-medium text-sm flex-1">{sc.name}</span>
                  <span className="text-xs text-gray-500">{cloneInfo?.grams_per_cut || '?'}g/corte</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={sc.percentage || ''}
                    onChange={(e) => onPercentage(sc.name, e.target.value)}
                    className="w-16 px-2 py-1 border rounded text-center text-sm"
                    placeholder="%"
                    data-testid={`clone-pct-${sc.name}`}
                  />
                  <span className="text-xs text-gray-500">%</span>
                  <button
                    onClick={() => onToggle(sc.name)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium px-3 py-1.5 rounded border border-red-300 transition-colors"
                    title={`Remover ${sc.name}`}
                    data-testid={`remove-clone-${sc.name}`}
                  >
                    Remover
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid de clones para seleção */}
      <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
        {sortedClones.map(clone => {
          const selected = isSelected(clone.name);
          return (
            <button
              key={clone.name}
              onClick={() => onToggle(clone.name)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                selected
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`clone-option-${clone.name}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{clone.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {clone.grams_per_cut}{t('wizardGramsPerCut') || 'g/corte'}
                    {popularClones.includes(clone.name) && (
                      <Badge className="ml-1 text-xs bg-amber-100 text-amber-700">{t('wizardPopular') || 'Popular'}</Badge>
                    )}
                  </div>
                </div>
                {selected && <span className="text-emerald-500 text-lg">{'\u2713'}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SystemStep = ({ formData, tappingSystems, onSelect }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <span className="text-4xl">🔄</span>
        <h3 className="text-lg font-semibold mt-2">{t('wizardTappingSystem') || 'Sistema de Sangria'}</h3>
        <p className="text-gray-600 text-sm">
          {t('wizardTappingFrequency') || 'Qual a frequência de sangria utilizada?'}
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 mb-4">
        <strong>💡 {t('tip') || 'Entenda'}:</strong> {t('wizardSystemTip') || 'D/4 significa sangria a cada 4 dias (4 lotes). Sistemas com mais dias entre sangrias preservam a árvore, mas reduzem a produção total.'}
      </div>

      <div className="space-y-2">
        {tappingSystems.map(sys => (
          <button
            key={sys.name}
            onClick={() => onSelect(sys.name)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              formData.tapping_system === sys.name
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`system-option-${sys.name}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{sys.name}</div>
                <div className="text-sm text-gray-500">{sys.description}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-emerald-600">
                  {sys.cuts_per_month || '~7-8'} {t('wizardCutsPerMonth') || 'cortes/mês'}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const TreesStep = ({ formData, onChange }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <span className="text-4xl">📊</span>
        <h3 className="text-lg font-semibold mt-2">{t('wizardTreesData') || 'Dados das Árvores'}</h3>
        <p className="text-gray-600 text-sm">
          {t('wizardTreesDataDesc') || 'Informe a quantidade e idade do seringal'}
        </p>
      </div>

      <div className="space-y-6">
        {/* Árvores em produção - Campo principal */}
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Label className="text-emerald-800 font-semibold">{t('wizardTreesInProduction') || 'Total de Árvores em Produção'} *</Label>
          <p className="text-xs text-emerald-600 mb-2">
            {t('wizardTreesInProductionHint') || 'Quantas árvores estão sendo sangradas atualmente?'}
          </p>
          <Input 
            type="number"
            value={formData.trees_in_production || ''}
            onChange={(e) => onChange('trees_in_production', parseInt(e.target.value) || 0)}
            placeholder="Ex: 5000"
            className="text-lg"
            data-testid="trees-in-production-input"
          />
        </div>

        <div className="grid grid-cols-1 gap-4">
          {/* Densidade - baseada em espaçamento */}
          <div>
            <Label>Arvores por Hectare (Densidade)</Label>
            <p className="text-xs text-gray-500 mb-2">Selecione o espacamento ou insira manualmente</p>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[
                { label: '7m x 3m', density: 476, desc: 'Mais comum' },
                { label: '8m x 2.5m', density: 500, desc: 'Adensado' },
                { label: '6m x 3m', density: 555, desc: 'Intensivo' },
                { label: '7m x 2.5m', density: 571, desc: 'Alta densidade' },
                { label: '8m x 3m', density: 416, desc: 'Amplo' },
                { label: '6m x 2.5m', density: 666, desc: 'Super adensado' },
              ].map(opt => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => onChange('tree_density', opt.density)}
                  className={`p-2 rounded-lg border-2 text-left transition-all ${
                    formData.tree_density === opt.density
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`density-${opt.density}`}
                >
                  <div className="font-medium text-sm">{opt.label}</div>
                  <div className="text-xs text-gray-500">{opt.density} arv/ha</div>
                  <div className="text-xs text-gray-400">{opt.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">Personalizado:</span>
              <Input 
                type="number"
                min="200"
                max="1000"
                value={formData.tree_density || ''}
                onChange={(e) => onChange('tree_density', parseInt(e.target.value) || 0)}
                placeholder="Ex: 450"
                className="w-32"
                data-testid="tree-density-input"
              />
              <span className="text-xs text-gray-500">arv/ha</span>
            </div>
          </div>

          {/* Idade */}
          <div>
            <Label>{t('wizardSeringalAge') || 'Idade do Seringal (anos)'}</Label>
            <p className="text-xs text-gray-500 mb-2">{t('wizardSeringalAgeHint') || 'Anos de exploração'}</p>
            <Input 
              type="number"
              value={formData.seringal_age_years || 7}
              onChange={(e) => onChange('seringal_age_years', parseInt(e.target.value) || 7)}
              data-testid="seringal-age-input"
            />
          </div>

          {/* DRC — Teor de Borracha Seca */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <Label className="text-amber-800 font-semibold">🧪 DRC — Teor de Borracha Seca (%)</Label>
            <p className="text-xs text-amber-600 mb-2">
              Converte o coágulo úmido (que você pesa na entrega) em borracha seca para comparar com as metas dos clones.
              Padrão de mercado (CVG): 53%. Ajuste conforme os recibos da sua usina.
            </p>
            <Input
              type="number"
              min="30"
              max="100"
              step="0.5"
              value={formData.drc_percentage ?? 53}
              onChange={(e) => onChange('drc_percentage', parseFloat(e.target.value) || 53)}
              className="w-32"
              data-testid="wizard-drc-input"
            />
          </div>
        </div>

        {/* Feedback do que falta */}
        {(formData.trees_in_production <= 0 || formData.tree_density <= 0) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <strong>Para avancar, preencha:</strong>
            <ul className="mt-1 ml-4 list-disc">
              {formData.trees_in_production <= 0 && <li>Total de arvores em producao</li>}
              {formData.tree_density <= 0 && <li>Selecione um espacamento ou insira a densidade</li>}
            </ul>
          </div>
        )}

        {/* Cálculo automático */}
        {formData.trees_in_production > 0 && formData.tree_density > 0 && (
          <div className="p-3 bg-emerald-100 rounded-lg text-sm text-emerald-800">
            <strong>{t('wizardEstimatedArea') || 'Area estimada:'}</strong>{' '}
            {(formData.trees_in_production / formData.tree_density).toFixed(1)} {t('wizardHectares') || 'hectares'}
            {' '} {'\u2713'}
          </div>
        )}
      </div>
    </div>
  );
};

const ExtrasStep = ({ formData, onChange }) => {
  const { t } = useTranslation();
  
  const regions = [
    { value: 'sp', labelKey: 'wizardRegionSP', descKey: 'wizardRegionSPDesc' },
    { value: 'es', labelKey: 'wizardRegionES', descKey: 'wizardRegionESDesc' },
    { value: 'mt', labelKey: 'wizardRegionMT', descKey: 'wizardRegionMTDesc' }
  ];
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <span className="text-4xl">⚙️</span>
        <h3 className="text-lg font-semibold mt-2">{t('wizardAdditionalDetails') || 'Detalhes Adicionais'}</h3>
        <p className="text-gray-600 text-sm">
          {t('wizardAdditionalDetailsDesc') || 'Região e uso de estimulação química'}
        </p>
      </div>

      <div className="space-y-6">
        {/* Região */}
        <div className="p-4 border rounded-lg">
          <Label className="font-semibold">🗺️ {t('wizardRegion') || 'Região'}</Label>
          <p className="text-xs text-gray-500 mb-3">
            {t('wizardRegionExplain') || 'A região afeta os coeficientes sazonais de produção'}
          </p>
          
          <div className="space-y-2">
            {regions.map(region => (
              <button
                key={region.value}
                onClick={() => onChange('region', region.value)}
                className={`w-full p-3 rounded-lg border text-left transition-all ${
                  formData.region === region.value
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`region-option-${region.value}`}
              >
                <div className="font-medium text-sm">{t(region.labelKey) || region.labelKey}</div>
                <div className="text-xs text-gray-500">{t(region.descKey) || region.descKey}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Estimulação */}
        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="font-semibold">💉 {t('wizardStimulation') || 'Usa Estimulação (Ethephon)?'}</Label>
              <p className="text-xs text-gray-500">{t('wizardStimulationHint') || 'Pode aumentar produção em até 28%'}</p>
            </div>
            <Switch 
              checked={formData.uses_stimulation}
              onCheckedChange={(v) => {
                onChange('uses_stimulation', v);
                if (!v) onChange('stimulation_percentage', null);
              }}
              data-testid="stimulation-switch"
            />
          </div>

          {formData.uses_stimulation && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg">
              <Label className="text-amber-800">{t('wizardStimulationConcentration') || 'Concentração do Ethephon (%)'}</Label>
              <Select 
                value={String(formData.stimulation_percentage || 2.5)}
                onValueChange={(v) => onChange('stimulation_percentage', parseFloat(v))}
              >
                <SelectTrigger data-testid="stimulation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2.5">{t('wizardStimulationLow') || '2.5% (Baixo risco, +15-20%)'}</SelectItem>
                  <SelectItem value="3.3">{t('wizardStimulationMedium') || '3.3% (Moderado, +20-25%)'}</SelectItem>
                  <SelectItem value="5.0">{t('wizardStimulationHigh') || '5.0% (Alto risco, +25-28%)'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmStep = ({ formData, clones, tappingSystems }) => {
  const { t } = useTranslation();
  const selectedClones = formData.clones || [];
  
  // Calcular média ponderada de g/corte
  const weightedGrams = selectedClones.reduce((sum, sc) => {
    const info = clones.find(c => c.name === sc.name);
    return sum + ((info?.grams_per_cut || 0) * (sc.percentage / 100));
  }, 0);

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mt-2">{t('wizardConfirmData') || 'Confirme os Dados'}</h3>
        <p className="text-gray-600 text-sm">
          {t('wizardConfirmDataDesc') || 'Revise as informações antes de salvar'}
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex justify-between py-2 border-b">
          <span className="text-gray-600">{t('property') || 'Propriedade'}</span>
          <span className="font-medium">{formData.property_name}</span>
        </div>
        
        {/* Clones - multi */}
        <div className="py-2 border-b">
          <span className="text-gray-600">{selectedClones.length > 1 ? 'Clones' : 'Clone'}</span>
          <div className="mt-2 space-y-1">
            {selectedClones.map(sc => {
              const info = clones.find(c => c.name === sc.name);
              return (
                <div key={sc.name} className="flex justify-between items-center bg-white p-2 rounded border">
                  <span className="font-medium text-sm">{sc.name}</span>
                  <span className="text-sm text-gray-600">
                    {sc.percentage}% - {info?.grams_per_cut || '?'}g/corte
                  </span>
                </div>
              );
            })}
          </div>
          {selectedClones.length > 1 && (
            <div className="mt-2 text-sm text-emerald-700 font-medium">
              Media ponderada: {weightedGrams.toFixed(1)}g/corte
            </div>
          )}
        </div>
        
        <div className="flex justify-between py-2 border-b">
          <span className="text-gray-600">{t('tappingSystem') || 'Sistema'}</span>
          <span className="font-medium">{formData.tapping_system}</span>
        </div>
        
        <div className="flex justify-between py-2 border-b">
          <span className="text-gray-600">{t('wizardTreesInProduction') || 'Arvores em Producao'}</span>
          <span className="font-medium">{formData.trees_in_production?.toLocaleString()}</span>
        </div>
        
        <div className="flex justify-between py-2 border-b">
          <span className="text-gray-600">{t('wizardSeringalAge') || 'Idade do Seringal'}</span>
          <span className="font-medium">{formData.seringal_age_years} {t('years') || 'anos'}</span>
        </div>
        
        <div className="flex justify-between py-2 border-b">
          <span className="text-gray-600">{t('wizardRegion') || 'Regiao'}</span>
          <span className="font-medium">{t(`wizardRegion${formData.region?.toUpperCase()}`) || formData.region}</span>
        </div>
        
        <div className="flex justify-between py-2">
          <span className="text-gray-600">{t('wizardStimulation')?.replace('?', '') || 'Estimulacao'}</span>
          <span className="font-medium">
            {formData.uses_stimulation 
              ? `${t('wizardYes') || 'Sim'} (${formData.stimulation_percentage}%)` 
              : (t('wizardNo') || 'Nao')}
          </span>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
        <strong>{t('wizardWhatHappensNext') || 'O que acontece agora?'}</strong>
        <p className="mt-1">
          {t('wizardWhatHappensNextDesc') || 'Com o contexto configurado, o sistema calculara suas metas justas baseadas nestes dados. Voce podera acompanhar seu desempenho no Dashboard de Desempenho Contextual.'}
        </p>
      </div>
    </div>
  );
};

export default ProductionContextWizard;
